import { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';

import {
  GenericWizardModalBody,
  GenericWizardSection,
} from 'components/GenericWizard';
import MultiSelect from 'components/Inputs/MultiSelect';
import { ButtonGroupImageButton } from 'components/Inputs/ButtonGroup/Button';
import { CONTEXTS } from 'components/Wizards/utils';
import { useTranslation } from 'react-i18next';
import KizenTypography from 'app/kizentypo';
import { isGroupFilter, isCustomFilter, isLLMDecisionFilter } from './types';

import TimeZoneFilterWarning from 'components/Wizards/MetaFilters/TimeZoneFilterWarning';
import { useWizardFilterContext } from 'components/Wizards/MetaFilters/context';
import {
  GroupPill,
  useCanViewCustomObject,
  getGroupLink,
} from 'ts-components/filters/GroupPill';
import { FilterSets } from './FilterSets';
import { useRelatedGroupsMatchHelper } from 'components/Wizards/MetaFilters/hooks/useRelatedGroupsMatchHelper';
import { useUserDescriptionContext } from 'pages/AutomationEngine/dialogs/UserDescription/userDescriptionContext';
import { UserDescription } from 'pages/AutomationEngine/dialogs/UserDescription';
import { DescriptionSection } from 'pages/AutomationEngine/dialogs/UserDescription/styles';
import { EMPTY_ARRAY } from 'utility/fieldHelpers';
import { LLMDecision } from './LLMDecision';
import { VariableWarning } from './VariableWarning';
import { useScrollMenuIntoView } from 'hooks/useScrollMenuIntoView';

const FilterWrapper = styled.div`
  margin-top: -15px;
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ rows = 3 }) => rows}, 1fr);
  column-gap: 20px;
`;

const GroupSelect = styled(MultiSelect)`
  margin-bottom: 15px;
`;

export const MyRecordsFilterWarning = ({
  type,
  groups,
  hasMyRecordFilter,
  hideWarnings = false,
  isMyRecordsResultToZero = false,
}) => {
  const { t } = useTranslation();
  const isCustomFilterType = isCustomFilter(type);

  const showWarning = useMemo(() => {
    const show =
      !hideWarnings &&
      (isCustomFilterType
        ? hasMyRecordFilter
        : groups.some(({ isMyRecordsGroup }) => isMyRecordsGroup));
    return show
      ? {
          result: isMyRecordsResultToZero
            ? t('return zero results')
            : t('be ignored'),
          filterType: isCustomFilterType ? t('filters') : t('groups'),
        }
      : null;
  }, [
    isCustomFilterType,
    groups,
    hasMyRecordFilter,
    hideWarnings,
    isMyRecordsResultToZero,
    t,
  ]);

  return showWarning ? (
    <KizenTypography fontStyle="italic" color="var(--color-viz-cat-7-primary)">
      <br />*
      {t(
        'One of the {{filterType}} contains a "My Records" filter that will {{result}}.',
        showWarning
      )}
    </KizenTypography>
  ) : null;
};

const FilterWizardBody = ({
  hideWarnings,
  area,
  additionalFields,
  disabled,
  footer,
  renderAIComponent,
  hideGroups = false,
  hideLLMDecision = false,
  isMyRecordsResultToZero,
  defaultCustomFilter,
  intitialRenderRef,
  showDescription = false,
  mergeFields = EMPTY_ARRAY,
  className,
}) => {
  const { t } = useTranslation();
  const {
    instance,
    filterSets,
    filterOps,
    conditionTypeOptions,
    groupOptions,
    loading,
    setValues,
    values,
    customObject,
  } = useWizardFilterContext();
  const { hasMyRecordFilter, hasStageRecordFilter, hasVariableValue } =
    filterOps;

  const { userDescription, setUserDescription } = useUserDescriptionContext();

  const { id: customObjectId, fetchUrl } = customObject || {};
  const canViewCustomObject = useCanViewCustomObject(customObjectId);

  const groupError = useMemo(
    () => (values?.groups || []).some(({ deleted }) => deleted),
    [values]
  );

  const { groupIsMe, groupIsStage } = useRelatedGroupsMatchHelper(filterSets);

  const PillComponent = useMemo(() => {
    if (canViewCustomObject) {
      return ({ value, ...rest }) => {
        const href = getGroupLink(value, fetchUrl, customObjectId);
        return <GroupPill {...rest} value={value.id} href={href} />;
      };
    }
  }, [canViewCustomObject, customObjectId, fetchUrl]);

  const filteredOptions = useMemo(() => {
    return conditionTypeOptions.filter(({ value }) => {
      if (isGroupFilter(value)) {
        return !hideGroups;
      }
      if (isLLMDecisionFilter(value)) {
        return !hideLLMDecision;
      }
      return true;
    });
  }, [conditionTypeOptions, hideGroups, hideLLMDecision]);

  if (
    (defaultCustomFilter || !values.type) &&
    area === CONTEXTS.fieldsLayout &&
    intitialRenderRef.current &&
    filteredOptions.length === 1 &&
    !values.type &&
    !loading
  ) {
    setValues({ ...values, type: filteredOptions[0].value });
    intitialRenderRef.current = false;
  }

  const onChangeLLMDecision = useCallback(
    (data) => {
      setValues({
        ...values,
        llmDecision: {
          ...values.llmDecision,
          ...data,
        },
      });
    },
    [setValues, values]
  );

  const showVariableWarning =
    instance === 'automations_condition_step' &&
    values.type === 'custom_filter' &&
    hasVariableValue;

  const containerRef = useScrollMenuIntoView(!loading && values.type);

  if (loading) {
    return null;
  }

  return (
    <GenericWizardModalBody containerRef={containerRef} className={className}>
      <GenericWizardSection header={t('Choose Condition Type')} isActive>
        {additionalFields}
        <ButtonGrid rows={Math.max(filteredOptions.length, 3)}>
          {filteredOptions.map((option) => (
            <ButtonGroupImageButton
              key={option.value}
              selected={option.value === values.type}
              onClick={() => {
                setValues({ ...values, type: option.value });
              }}
              option={option}
              disabled={disabled}
              data-qa={`condition-type-${option.value}`}
            />
          ))}
        </ButtonGrid>
      </GenericWizardSection>
      {showDescription && values.type ? (
        <DescriptionSection
          header={t('Description')}
          headerType="header"
          isActive={!!values.type}
        >
          <UserDescription
            userDescription={userDescription}
            setUserDescription={setUserDescription}
            isActive={!!values.type}
          />
        </DescriptionSection>
      ) : null}
      <GenericWizardSection
        header={t('Condition Settings')}
        isActive={values.type}
        leavePAlone
      >
        {values.type ? (
          <>
            {isGroupFilter(values.type) && (
              <GroupSelect
                label={
                  values.type === 'in_group'
                    ? t('Is In Group(s)')
                    : t('Is Not In Group(s)')
                }
                placeholder={t('Choose Group(s)')}
                menuInline
                options={groupOptions}
                value={(values.groups || []).map((g) => g.id)}
                PillComponent={PillComponent}
                onChange={(opts) =>
                  setValues({
                    ...values,
                    groups: opts.map((opt) => opt.group),
                  })
                }
                error={groupError}
                captureMenuScroll={false}
              />
            )}
            {isCustomFilter(values.type) && (
              <FilterWrapper>
                <FilterSets />
              </FilterWrapper>
            )}
            {isLLMDecisionFilter(values.type) && (
              <LLMDecision
                llmDecision={values.llmDecision}
                mergeFields={mergeFields}
                onChange={onChangeLLMDecision}
                renderAIComponent={renderAIComponent}
                errors={{}}
              />
            )}
          </>
        ) : null}

        <MyRecordsFilterWarning
          hideWarnings={hideWarnings?.isMeWarning}
          isMyRecordsResultToZero={isMyRecordsResultToZero}
          hasMyRecordFilter={hasMyRecordFilter || groupIsMe}
          {...values}
        />
        <TimeZoneFilterWarning
          hideWarnings={hideWarnings?.timezoneWarning}
          {...values}
          hasStageRecordFilter={hasStageRecordFilter || groupIsStage}
        />
        {showVariableWarning && <VariableWarning />}
        {footer}
      </GenericWizardSection>
    </GenericWizardModalBody>
  );
};

export default FilterWizardBody;
